import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { propertyService } from 'api/services/propertyService';
import { shortageService } from 'api/services/shortageService';
import { useShortageBoard } from 'hooks/api/useShortageBoard';
import {
  SHORTAGE_ACTION_LABELS,
  SHORTAGE_LEVER_LABELS,
} from 'api/types/shortage.types';
import type {
  ShortageAffectedStay,
  ShortageNight,
  ShortagePlanDetail,
} from 'api/types/shortage.types';

// ============================================================================
// HELPERS
// ============================================================================

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDateInput = (d: Date): string => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};

const formatDay = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const nightKey = (n: ShortageNight): string => `${n.roomCategoryId}|${n.night.slice(0, 10)}`;

/**
 * Levers are ranked by how much room to manoeuvre they leave, matching the
 * server's ordering. Colour follows the same idea rather than severity: green is
 * "there is something easy to do here", grey is "there is nothing left to decide".
 */
const LEVER_STYLE: Record<string, { bg: string; fg: string }> = {
  EXPIRING_HOLD:   { bg: 'rgba(22, 163, 74, 0.18)',  fg: 'var(--success, #16a34a)' },
  NOT_YET_FIRM:    { bg: 'rgba(37, 99, 235, 0.18)',  fg: 'var(--primary, #2563eb)' },
  CATEGORY_SHIFT:  { bg: 'rgba(22, 163, 74, 0.18)',  fg: 'var(--success, #16a34a)' },
  ADVANCE_OVERDUE: { bg: 'rgba(217, 119, 6, 0.18)',  fg: 'var(--warning, #d97706)' },
  CONSOLIDATION:   { bg: 'rgba(217, 119, 6, 0.18)',  fg: 'var(--warning, #d97706)' },
  SISTER_PROPERTY: { bg: 'rgba(217, 119, 6, 0.18)',  fg: 'var(--warning, #d97706)' },
  LOCKED:          { bg: 'rgba(0, 0, 0, 0.08)',      fg: 'var(--text-muted)' },
};

/** Upgrade reads as a win, downgrade as a negotiation. Say which, not just "move". */
const DIRECTION_MARK: Record<string, string> = {
  UPGRADE: '↑',
  DOWNGRADE: '↓',
  LATERAL: '→',
};

const DETAIL_BADGE: Record<string, string> = {
  PLAN_PLANNED: 'badge-pending',
  PLAN_EXECUTED: 'badge-active',
  PLAN_ABANDONED: 'badge-inactive',
};

// ============================================================================
// COMPONENT
// ============================================================================

interface ShortageBoardScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'view','plan','resolve','export'} the current user may perform —
   * driven by admin.tbl_role_permissions. Defaults to [] (fail-closed).
   */
  allowedActions?: string[];
}

/**
 * The standing view of what the property owes beyond its inventory.
 *
 * Everything on this screen is DERIVED from live inventory (design invariant I1):
 * a cancellation, date change or early departure makes a row disappear on the next
 * load with nothing to reconcile. Nothing here changes inventory either (I2) —
 * planning records a decision, and the resolutions run through the operations that
 * already own them. That is why a plan can never close a shortage: only the real
 * transaction can, and then the night simply stops appearing.
 */
const ShortageBoardScreen: React.FC<ShortageBoardScreenProps> = ({ moduleIcon, allowedActions = [] }) => {
  const today = useMemo(() => new Date(), []);

  const [selectedProperty, setSelectedProperty] = useState('');
  const [dateFrom, setDateFrom] = useState(toDateInput(today));
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 60);
    return toDateInput(d);
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /** In-progress remark text, keyed by night+booking so each row edits alone. */
  const [remarkDraft, setRemarkDraft] = useState<Record<string, string>>({});

  const [executeTarget, setExecuteTarget] = useState<ShortagePlanDetail | null>(null);
  const [executedRef, setExecutedRef] = useState('');

  const query = useMemo(
    () => (selectedProperty ? { propertyId: selectedProperty, dateFrom, dateTo } : null),
    [selectedProperty, dateFrom, dateTo],
  );
  const { data: nights, loading, error: boardError, refetch } = useShortageBoard(query);

  const canPlan = allowedActions.includes('plan');
  const canResolve = allowedActions.includes('resolve');

  const loadPropertyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({
      searchTerm: inputValue || undefined,
      pageSize: inputValue ? 50 : 500,
      includeInactive: false,
    });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  // ── Planning ────────────────────────────────────────────────────────────

  /**
   * Records a decision. The plan header is opened lazily on the first line: a
   * manager should not have to "create a plan" before saying what they intend to
   * do, and create-or-get on the server means two managers working the same night
   * land on the same worksheet rather than two competing ones.
   */
  const handleRecordRemark = useCallback(async (
    night: ShortageNight,
    stay: ShortageAffectedStay,
  ) => {
    if (!canPlan || !selectedProperty) return;
    const day0 = night.night.slice(0, 10);
    const remark = (remarkDraft[`${day0}|${stay.bookingId}`] ?? '').trim();
    if (!remark) return;
    const key = `${nightKey(night)}|${stay.bookingId}`;
    setBusyKey(key);
    try {
      const day = night.night.slice(0, 10);
      const plan = await shortageService.getOrCreatePlan({
        propertyId: selectedProperty,
        roomCategoryId: night.roomCategoryId,
        dateFrom: day,
        dateTo: day,
      });
      await shortageService.upsertDetail({
        shortagePlanId: plan.id,
        shortageDate: day,
        affectedBookingId: stay.bookingId,
        notes: remark,
      });
      setRemarkDraft((prev) => {
        const next = { ...prev };
        delete next[`${day}|${stay.bookingId}`];
        return next;
      });
      notificationService.toast.success(
        `Decision recorded for ${stay.bookingNumber} on ${formatDay(day)}.`,
      );
      await refetch();
    } catch (err) {
      notificationService.toast.error((err as Error).message || 'Could not record the decision.');
    } finally {
      setBusyKey(null);
    }
  }, [canPlan, selectedProperty, refetch, remarkDraft]);

  const handleConfirmExecute = useCallback(async () => {
    if (!executeTarget) return;
    setBusyKey(executeTarget.id);
    try {
      await shortageService.executeDetail(executeTarget.id, {
        executedRef: executedRef.trim() || null,
        rowVersion: executeTarget.rowVersion,
      });
      notificationService.toast.success('Marked as executed. The board will show the shortage as it now stands.');
      setExecuteTarget(null);
      setExecutedRef('');
      await refetch();
    } catch (err) {
      notificationService.toast.error((err as Error).message || 'Could not mark the line executed.');
    } finally {
      setBusyKey(null);
    }
  }, [executeTarget, executedRef, refetch]);

  const handleAbandon = useCallback(async (detail: ShortagePlanDetail) => {
    setBusyKey(detail.id);
    try {
      await shortageService.abandonDetail(detail.id, detail.rowVersion);
      notificationService.toast.success('Plan line abandoned.');
      await refetch();
    } catch (err) {
      notificationService.toast.error((err as Error).message || 'Could not abandon the line.');
    } finally {
      setBusyKey(null);
    }
  }, [refetch]);

  const handleExport = useCallback(() => {
    const rows = [
      ['Night', 'Category', 'Total', 'Booked', 'Available', 'Oversold', 'Undecided bookings', 'Scope', 'Free elsewhere', 'Property short', 'Booking', 'Guest', 'Rooms', 'Status', 'Levers', 'Planned action', 'Category options', 'Sister property options', 'Consolidation'],
      ...nights.flatMap((n) => {
        const plannedFor = new Map(n.planDetails.map((d) => [d.affectedBookingId, d]));
        return n.affectedStays.map((s) => [
          n.night.slice(0, 10), n.categoryName, n.totalRooms, n.bookedRooms,
          n.availableRooms, n.oversoldRooms, n.undecidedBookings,
          n.scope === 'PROPERTY_WIDE' ? 'Property-wide' : 'Category only',
          n.propertyAvailableRooms, n.propertyShortfallRooms,
          s.bookingNumber, s.guestName ?? '', s.roomsInCategory, s.bookingStatus ?? s.bookingStatusCode,
          s.levers.map((l) => SHORTAGE_LEVER_LABELS[l]).join(' / '),
          plannedFor.get(s.bookingId)?.proposedAction ?? '',
          // The export is what gets taken into a morning meeting, so it carries
          // the options in full rather than the truncated on-screen preview.
          (s.categoryShiftOptions ?? []).map((o) => `${o.categoryName} (${o.minAvailableRooms})`).join(' / '),
          (s.sisterPropertyOptions ?? []).map((o) => `${o.propertyName}: ${o.categoryName} (${o.minAvailableRooms})`).join(' / '),
          s.consolidation
            ? `${s.consolidation.partyAdults}+${s.consolidation.partyChildren} pax fit ${s.consolidation.minRoomsNeeded} of ${s.consolidation.roomsHeld} rooms, frees ${s.consolidation.roomsFreed}`
            : '',
        ]);
      }),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `shortage-board-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [nights, dateFrom, dateTo]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void refetch(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export') && nights.length > 0) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={16} />, label: 'Export', onClick: handleExport, variant: 'secondary' });
    }
    return buttons;
  }, [allowedActions, nights.length, handleExport, refetch]);

  const totalOversold = useMemo(() => nights.reduce((sum, n) => sum + n.oversoldRooms, 0), [nights]);
  const totalUndecided = useMemo(() => nights.reduce((sum, n) => sum + n.undecidedBookings, 0), [nights]);
  /**
   * Nights the BUILDING is short, not just a category. These are the rows that
   * need a relocation or a walk — everything else can be absorbed by moving
   * someone between categories.
   */
  const propertyWideNights = useMemo(
    () => nights.filter((n) => n.scope === 'PROPERTY_WIDE').length,
    [nights],
  );

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Shortage Board"
        description="Nights where committed demand exceeds inventory, the stays exposed on them, and what the property has decided to do."
        icon={<IconSystem.warning size={18} />}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem' }}>

            {!selectedProperty && (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Choose a property in the filter panel to load the board.
              </div>
            )}

            {selectedProperty && loading && (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Loading…
              </div>
            )}

            {selectedProperty && !loading && boardError && (
              /* A board that cannot load MUST NOT render the reassuring empty
                 state. "Every night is within inventory" and "we have no idea"
                 look identical to a user and mean opposite things — and this is
                 the screen that decides whether a guest gets walked. Fail loud. */
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <div style={{ color: 'var(--danger, #dc2626)', fontSize: '0.9rem', fontWeight: 700 }}>
                  Shortage could not be loaded — this is NOT an all-clear.
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '0.5rem', maxWidth: '46rem', margin: '0.5rem auto 0', lineHeight: 1.5 }}>
                  {boardError.message || 'The request failed.'} Treat this window as unknown
                  and check inventory directly until the board loads.
                </div>
                <button className="btn btn-secondary" style={{ marginTop: '0.9rem' }} onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            )}

            {selectedProperty && !loading && !boardError && nights.length === 0 && (
              /* Not an empty state to apologise for — it is the answer the
                 property wants. Say it plainly rather than showing "no records".
                 The second line matters: a SOLD-OUT category is not a shortage,
                 and without saying so an empty board reads as broken to anyone
                 who has just seen "0 of 1 available" on a booking screen. */
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <div style={{ color: 'var(--success, #16a34a)', fontSize: '0.9rem', fontWeight: 600 }}>
                  No shortage in this window. Every night is within inventory.
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '0.5rem', maxWidth: '46rem', margin: '0.5rem auto 0', lineHeight: 1.5 }}>
                  This board lists nights where committed rooms EXCEED inventory. A category
                  that is merely sold out — 0 available, nothing oversold — is fully booked,
                  not short, and does not appear here. Rows appear once a booking or a
                  check-in is deliberately accepted beyond available inventory.
                </div>
              </div>
            )}

            {selectedProperty && !loading && nights.length > 0 && (
              <>
                <div style={{
                  display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '0.75rem',
                  padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
                  fontSize: '0.8rem',
                }}>
                  <span><strong>{nights.length}</strong> short night{nights.length === 1 ? '' : 's'}</span>
                  <span style={{ color: 'var(--danger, #dc2626)' }}><strong>{totalOversold}</strong> room-night(s) oversold</span>
                  <span style={{ color: totalUndecided > 0 ? 'var(--warning, #d97706)' : 'var(--text-muted)' }}>
                    <strong>{totalUndecided}</strong> booking(s) with no decision recorded
                  </span>
                  <span style={{ color: propertyWideNights > 0 ? 'var(--danger, #dc2626)' : 'var(--text-muted)' }}>
                    <strong>{propertyWideNights}</strong> night(s) the property itself is short
                  </span>
                </div>

                {nights.map((night) => {
                  const key = nightKey(night);
                  const isOpen = expanded[key] ?? true;
                  const plannedFor = new Map(night.planDetails.map((d) => [d.affectedBookingId, d]));

                  return (
                    <div key={key} style={{
                      border: '1px solid var(--border-color)', borderRadius: '0.5rem',
                      marginBottom: '0.75rem', overflow: 'hidden', background: 'var(--bg-primary)',
                    }}>
                      <div
                        onClick={() => setExpanded((p) => ({ ...p, [key]: !isOpen }))}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((p) => ({ ...p, [key]: !isOpen })); } }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem',
                          background: 'var(--bg-secondary)', cursor: 'pointer', userSelect: 'none',
                        }}
                      >
                        <IconSystem.chevronDown size={14} style={{ transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }} />
                        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{formatDay(night.night)}</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{night.categoryName}</span>
                        <span style={{
                          fontSize: '0.75rem', fontWeight: 700, color: 'var(--danger, #dc2626)',
                          background: 'rgba(220,38,38,0.15)', padding: '0.1rem 0.45rem', borderRadius: '0.25rem',
                        }}>
                          short {night.oversoldRooms}
                        </span>
                        {/* The distinction that decides the action. "Short 2" with
                            30 rooms free next door is an upgrade; "short 2" in a
                            full building is a guest being walked. Reporting both
                            the same way is what makes a board get ignored. */}
                        {night.scope === 'PROPERTY_WIDE' ? (
                          <span
                            style={{
                              fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.45rem',
                              borderRadius: '999px', whiteSpace: 'nowrap',
                              background: 'rgba(220,38,38,0.15)', color: 'var(--danger, #dc2626)',
                              border: '1px solid rgba(220,38,38,0.35)',
                            }}
                            title={`The property is short ${night.propertyShortfallRooms} room(s) this night — free rooms in other categories do not cover the total oversell`}
                          >
                            PROPERTY SHORT {night.propertyShortfallRooms}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: '0.68rem', fontWeight: 700, padding: '0.1rem 0.45rem',
                              borderRadius: '999px', whiteSpace: 'nowrap',
                              background: 'rgba(217,119,6,0.14)', color: 'var(--warning, #d97706)',
                              border: '1px solid rgba(217,119,6,0.32)',
                            }}
                            title={`${night.propertyAvailableRooms} room(s) free in other categories this night — the oversell can be absorbed inside the property`}
                          >
                            CATEGORY ONLY · {night.propertyAvailableRooms} free elsewhere
                          </span>
                        )}
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {night.bookedRooms} committed / {night.totalRooms} sellable
                          {' · '}
                          <strong>{night.availableRooms} available</strong>
                          {' · '}
                          <strong>{night.oversoldRooms} excess</strong>
                        </span>
                        {night.undecidedBookings > 0 && (
                          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 600, color: 'var(--warning, #d97706)' }}>
                            {night.undecidedBookings} undecided
                          </span>
                        )}
                      </div>

                      {isOpen && (
                        <div style={{ padding: '0.5rem 0.75rem 0.75rem' }}>
                          {night.affectedStays.length === 0 ? (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', padding: '0.5rem' }}>
                              No stays found on this night — the excess came from an inventory change rather than a booking.
                            </div>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                              <thead>
                                <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                                  <th style={{ padding: '0.3rem 0.4rem', fontWeight: 600 }}>Booking</th>
                                  <th style={{ padding: '0.3rem 0.4rem', fontWeight: 600 }}>Guest</th>
                                  <th style={{ padding: '0.3rem 0.4rem', fontWeight: 600 }}>Stay</th>
                                  <th style={{ padding: '0.3rem 0.4rem', fontWeight: 600, textAlign: 'center' }}>Rooms</th>
                                  <th style={{ padding: '0.3rem 0.4rem', fontWeight: 600 }}>Status</th>
                                  <th style={{ padding: '0.3rem 0.4rem', fontWeight: 600 }}>Levers</th>
                                  <th style={{ padding: '0.3rem 0.4rem', fontWeight: 600, minWidth: '230px' }}>Decision</th>
                                </tr>
                              </thead>
                              <tbody>
                                {night.affectedStays.map((stay) => {
                                  const detail = plannedFor.get(stay.bookingId);
                                  const rowBusy = busyKey === `${key}|${stay.bookingId}` || busyKey === detail?.id;
                                  /* Draft key is night-date + booking, matching what
                                     handleRecordRemark reads and clears. */
                                  const remarkKey = `${night.night.slice(0, 10)}|${stay.bookingId}`;
                                  return (
                                    <tr key={stay.bookingId} style={{ borderTop: '1px solid var(--border-color)' }}>
                                      <td style={{ padding: '0.4rem' }}>{stay.bookingNumber}</td>
                                      <td style={{ padding: '0.4rem' }}>{stay.guestName || '-'}</td>
                                      <td style={{ padding: '0.4rem', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                                        {formatDay(stay.checkInDate)} → {formatDay(stay.checkOutDate)}
                                      </td>
                                      <td style={{ padding: '0.4rem', textAlign: 'center', fontWeight: 600 }}>{stay.roomsInCategory}</td>
                                      <td style={{ padding: '0.4rem' }}>{stay.bookingStatus || stay.bookingStatusCode}</td>
                                      <td style={{ padding: '0.4rem' }}>
                                        {stay.levers.length === 0 ? (
                                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                                        ) : stay.levers.map((l) => (
                                          <span key={l} style={{
                                            display: 'inline-block', marginRight: '0.25rem', padding: '0.05rem 0.4rem',
                                            borderRadius: '0.25rem', fontSize: '0.68rem', fontWeight: 600,
                                            background: LEVER_STYLE[l]?.bg, color: LEVER_STYLE[l]?.fg,
                                          }}>
                                            {SHORTAGE_LEVER_LABELS[l]}
                                          </span>
                                        ))}

                                        {/* Where the stay could actually go. Named, with the
                                            binding night's free-room count, because "a category
                                            is free" is not a decision a manager can act on —
                                            "Suite, 3 free every night" is. */}
                                        {(stay.categoryShiftOptions?.length > 0 || stay.sisterPropertyOptions?.length > 0 || stay.consolidation) && (
                                          <div style={{ marginTop: '0.2rem', fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                            {stay.categoryShiftOptions?.slice(0, 3).map((o) => (
                                              <div key={o.roomCategoryId}>
                                                {DIRECTION_MARK[o.direction]} {o.categoryName}
                                                <span style={{ color: 'var(--text-muted)' }}> · {o.minAvailableRooms} free all nights</span>
                                              </div>
                                            ))}
                                            {stay.categoryShiftOptions?.length > 3 && (
                                              <div style={{ color: 'var(--text-muted)' }}>
                                                +{stay.categoryShiftOptions.length - 3} more categor
                                                {stay.categoryShiftOptions.length - 3 === 1 ? 'y' : 'ies'}
                                              </div>
                                            )}
                                            {stay.consolidation && (
                                              /* Spelled out rather than reduced to "frees 2":
                                                 a manager is about to phone a guest and ask them
                                                 to share, so they need to see the party and the
                                                 ceiling the arithmetic used. */
                                              <div>
                                                ⇥ {stay.consolidation.partyAdults} adult
                                                {stay.consolidation.partyAdults === 1 ? '' : 's'}
                                                {stay.consolidation.partyChildren > 0
                                                  ? ` + ${stay.consolidation.partyChildren} child${stay.consolidation.partyChildren === 1 ? '' : 'ren'}`
                                                  : ''}
                                                {' '}fit {stay.consolidation.minRoomsNeeded} room
                                                {stay.consolidation.minRoomsNeeded === 1 ? '' : 's'}
                                                <span style={{ color: 'var(--text-muted)' }}>
                                                  {' '}· max {stay.consolidation.maxOccupancy}/room · frees {stay.consolidation.roomsFreed}
                                                </span>
                                              </div>
                                            )}
                                            {stay.sisterPropertyOptions?.slice(0, 2).map((o) => (
                                              <div key={`${o.propertyId}-${o.roomCategoryId}`}>
                                                ⇢ {o.propertyName} · {o.categoryName}
                                                <span style={{ color: 'var(--text-muted)' }}> · {o.minAvailableRooms} free</span>
                                              </div>
                                            ))}
                                            {stay.sisterPropertyOptions?.length > 2 && (
                                              <div style={{ color: 'var(--text-muted)' }}>
                                                +{stay.sisterPropertyOptions.length - 2} more at other properties
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      <td style={{ padding: '0.4rem' }}>
                                        {detail && detail.detailStatusCode !== 'PLAN_ABANDONED' ? (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                            {/* The remark is what the desk wrote. Fall back to
                                                the coded label only for lines recorded before
                                                free text, or from a future remark master. */}
                                            <span className={`badge ${DETAIL_BADGE[detail.detailStatusCode ?? ''] ?? ''}`}
                                                  title={detail.notes ?? undefined}>
                                              {detail.notes?.trim()
                                                || SHORTAGE_ACTION_LABELS[detail.proposedAction]
                                                || detail.proposedAction}
                                            </span>
                                            {detail.detailStatusCode === 'PLAN_EXECUTED' ? (
                                              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                                done{detail.executedRef ? ` · ${detail.executedRef}` : ''}
                                              </span>
                                            ) : canResolve && (
                                              <>
                                                <button type="button" className="btn btn-sm btn-primary" disabled={rowBusy}
                                                  onClick={() => { setExecuteTarget(detail); setExecutedRef(''); }}>
                                                  Mark done
                                                </button>
                                                <button type="button" className="btn btn-sm btn-secondary" disabled={rowBusy}
                                                  onClick={() => handleAbandon(detail)}>
                                                  Drop
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        ) : canPlan ? (
                                          /* Free text, not a picked code. Nothing in the
                                             system branches on the decision, so a fixed
                                             vocabulary only made the desk choose a label
                                             that meant nothing downstream. A remark master
                                             can populate this later without changing what
                                             is stored. */
                                          <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                                            <input
                                              type="text"
                                              className="form-input"
                                              style={{ fontSize: '0.8125rem', padding: '0.3rem 0.5rem', minWidth: '11rem', flex: 1 }}
                                              placeholder="Record a decision…"
                                              disabled={rowBusy}
                                              value={remarkDraft[remarkKey] ?? ''}
                                              onChange={(e) => setRemarkDraft((prev) => ({ ...prev, [remarkKey]: e.target.value }))}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') { e.preventDefault(); handleRecordRemark(night, stay); }
                                              }}
                                            />
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-primary"
                                              disabled={rowBusy || !(remarkDraft[remarkKey] ?? '').trim()}
                                              onClick={() => handleRecordRemark(night, stay)}
                                              title={(remarkDraft[remarkKey] ?? '').trim() ? 'Save this decision' : 'Type a decision first'}
                                            >
                                              Save
                                            </button>
                                          </div>
                                        ) : (
                                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Property"
                isAsync
                loadOptions={loadPropertyOptions}
                defaultOptions
                cacheOptions={false}
                value={selectedProperty}
                onChange={(v) => setSelectedProperty((v as string) || '')}
                placeholder="Select property…"
                size="sm"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From</label>
              <input type="date" className="form-input" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To</label>
              <input type="date" className="form-input" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => {
              setDateFrom(toDateInput(today));
              const d = new Date(today); d.setDate(d.getDate() + 60);
              setDateTo(toDateInput(d));
            }}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!executeTarget}
        title="Mark as done?"
        message={
          'This records what was actually done. It does NOT change inventory — run the real operation '
          + '(room move, category change, cancellation, shorten stay) through its own screen. '
          + 'The board recomputes from inventory on the next load, so if the operation moved nothing, '
          + 'this night will still show short.'
        }
        confirmLabel="Mark done"
        cancelLabel="Cancel"
        variant="default"
        loading={busyKey === executeTarget?.id}
        loadingMessage="Saving…"
        onConfirm={handleConfirmExecute}
        onCancel={() => { setExecuteTarget(null); setExecutedRef(''); }}
      />
    </div>
  );
};

export default ShortageBoardScreen;
